import { Maximize2 } from "lucide-react";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { PinDirectionCone } from "@/components/common/pin-direction-cone/pin-direction-cone";
import { PinMarkerDot } from "@/components/common/pin-marker-dot/pin-marker-dot";
import { planAssetKey } from "@/offline/asset-keys";
import { cn } from "@/lib/utils";
import type { ProjectPlan } from "@/types/api";

/**
 * On-screen diameter of the marker on this tile. Matches the plan thumbnail in
 * the record viewer — a static surface, so the dot is deliberately smaller than
 * the 16px it takes on a zoomable plan.
 */
const PIN_DOT_SIZE_PX = 12;

export interface PlanPinThumbnailProps {
  plan: ProjectPlan;
  /** Pin position in plan-relative coords `[0,1]`. */
  pinX: number;
  pinY: number;
  /**
   * Plan-relative bearing in degrees `[0, 360)`, or null for an undirected pin
   * (see [[directional-pins]]). Drives the read-only cone; null draws none.
   */
  bearing: number | null;
  /** Tapping the tile — typically opens the plan full-screen on this pin. */
  onExpand: () => void;
  className?: string;
}

/**
 * The shared read-only **"where is this?" tile** — one plan sheet at thumbnail
 * size with one pin on it, and the view cone when that pin is directed.
 *
 * Read-only by construction: no rotate handle, no direction coachmark, no
 * tap-to-pin. Those belong to the editing surfaces (the record viewer's plan
 * block and the fullscreen editor); this component only ever *shows* a pin. See
 * [[plan-pin-thumbnail.spec]].
 */
export function PlanPinThumbnail({
  plan,
  pinX,
  pinY,
  bearing,
  onExpand,
  className,
}: PlanPinThumbnailProps) {
  const revision = plan.latest_revision;
  // Prefer the small rendering; fall back to the full image for a plan whose
  // thumbnail never generated. An absent src degrades to a bare frame with the
  // pin still on it — OfflineImage omits the attribute rather than emitting
  // `src=""`. See [[plan-pin-thumbnail.spec]] "Edge Cases".
  const src = revision?.thumbnail_url ?? revision?.image_url ?? "";

  return (
    <div className={cn("space-y-2", className)}>
      <p className="truncate text-sm text-muted-foreground">
        {plan.name}
        {plan.deleted_at != null ? " (Archived)" : ""}
      </p>
      <div className="relative w-full overflow-hidden rounded-md border bg-secondary/10">
        <OfflineImage
          assetKey={
            revision
              ? planAssetKey(plan.id, revision.id, revision.tile_status)
              : `plan:${plan.id}:none`
          }
          src={src}
          alt={plan.name}
          className="block h-auto w-full"
          draggable={false}
        />
        {/* View cone beneath the marker, anchored at the pin. Read-only — never
            the `preview` variant, which marks an uncommitted aim on an editing
            surface. An undirected pin renders no cone at all. See
            [[directional-pins]]. */}
        <div
          className="pointer-events-none absolute"
          style={{ left: `${pinX * 100}%`, top: `${pinY * 100}%` }}
        >
          <PinDirectionCone bearing={bearing} />
        </div>
        {/* The shared round dot — static here, so it does not ride
            `--pin-scale` (this tile has no zoom). See [[pin-marker-dot.spec]]. */}
        <PinMarkerDot
          xPct={pinX}
          yPct={pinY}
          size={PIN_DOT_SIZE_PX}
          scaleWithZoom={false}
        />
        {/* The whole tile is the tap target — the corner glyph is an
            affordance, not a second control, so there is one thing to hit on a
            gloved thumb. */}
        <button
          type="button"
          onClick={onExpand}
          aria-label={`Open ${plan.name} at this pin`}
          className="absolute inset-0 flex items-end justify-end p-2 transition-opacity hover:opacity-90"
        >
          <span
            className="rounded-md bg-black/50 p-1.5 text-white"
            aria-hidden="true"
          >
            <Maximize2 className="h-4 w-4" />
          </span>
        </button>
      </div>
    </div>
  );
}
